import React, { useEffect, useState, useRef } from 'react';
import { Card, Row, Col, Statistic, Progress, Spin, Alert, Tooltip, Button, List, Avatar, Select, Space, Input } from 'antd';
import { QuestionCircleOutlined, ReloadOutlined, BankOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getSchoolClassroomStatisticsOverall } from '../../core/school/schoolApi';
import { setSelectedSchoolStatistics } from '../../core/school/schoolSlice';
import { AppDispatch, CombinedStates } from '../../core/types';
import { schoolTypeOptions } from '../../core/utils/helpers';
import SchoolClassroomStatistic from '../SchoolClassroomStatistic';
import './style.scss';

const SchoolClassroomStatisticsOverall: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        overallClassroomStatistics,
        schoolClassroomStatistics,
        classroomStatistics,
        isFetching,
        error,
    } = useSelector((state: CombinedStates) => state.school);

    const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
    const [selectedSchoolType, setSelectedSchoolType] = useState<string | undefined>(undefined);
    const [searchText, setSearchText] = useState<string>('');
    const schoolInfoRef = useRef<HTMLDivElement>(null);

    const formatClassCount = (count: number): string => {
        if (count === 0) return 'klasių';
        if (count === 1) return 'klasė';
        if (count >= 2 && count <= 9) return 'klasės';
        return 'klasių';
    };

    const getSchoolIconColor = (schoolType: string): string => {
        const schoolTypeOption = schoolTypeOptions.find(opt => opt.value === schoolType);
        return schoolTypeOption?.color || '#fdab01'; // default blue
    };

    const fetchStatistics = () => {
        dispatch(getSchoolClassroomStatisticsOverall());
    };

    const handleSchoolClick = (schoolId: number, statistics: any) => {
        setSelectedSchoolId(schoolId);
        dispatch(setSelectedSchoolStatistics(statistics));

        // Scroll to school info component after a short delay to ensure it's rendered
        setTimeout(() => {
            if (schoolInfoRef.current) {
                schoolInfoRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        }, 100);
    };

    const handleSchoolTypeFilter = (value: string | undefined) => {
        setSelectedSchoolType(value);
        // Clear selected school if it doesn't match the new filter
        if (selectedSchoolId && value && schoolClassroomStatistics) {
            const selectedSchool = schoolClassroomStatistics.find(s => s.school_id === selectedSchoolId);
            if (selectedSchool && selectedSchool.school_type !== value) {
                setSelectedSchoolId(null);
                dispatch(setSelectedSchoolStatistics(null));
            }
        }
    };

    const handleResetFilter = () => {
        setSelectedSchoolType(undefined);
        setSearchText('');
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setSearchText(value);
        // Clear selected school if it doesn't match the new search
        if (selectedSchoolId && value) {
            const searchLower = value.toLowerCase();
            const selectedSchool = schoolClassroomStatistics?.find(s => s.school_id === selectedSchoolId);
            if (selectedSchool && !selectedSchool.school_name.toLowerCase().includes(searchLower)) {
                setSelectedSchoolId(null);
                dispatch(setSelectedSchoolStatistics(null));
            }
        }
    };

    // Filter schools based on selected type and search text
    const filteredSchools = schoolClassroomStatistics?.filter(school => {
        const matchesType = !selectedSchoolType || school.school_type === selectedSchoolType;
        const matchesSearch = !searchText || school.school_name.toLowerCase().includes(searchText.toLowerCase());
        return matchesType && matchesSearch;
    }) || [];

    useEffect(() => {
        fetchStatistics();
    }, [dispatch]);

    if (isFetching && !overallClassroomStatistics) {
        return (
            <div className="school-classroom-statistics-overall">
                <Card>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: '10px' }}>Kraunama bendra klasės statistika...</p>
                    </div>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="school-classroom-statistics-overall">
                <Card>
                    <Alert
                        message="Klaida"
                        description="Nepavyko užkrauti bendros klasės statistikos"
                        type="error"
                        showIcon />
                </Card>
            </div>
        );
    }

    if (!overallClassroomStatistics) {
        return (
            <div className="school-classroom-statistics-overall">
                <Card>
                    <Alert
                        message="Informacija"
                        description="Bendra klasės statistika nerasta"
                        type="info"
                        showIcon />
                </Card>
            </div>
        );
    }

    return (
        <Card>
            <div className="school-classroom-statistics-overall">
                {/* Reload button */}
                <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                    <Tooltip title="Atnaujinti statistiką">
                        <Button
                            type="default"
                            icon={<ReloadOutlined />}
                            onClick={fetchStatistics}
                            loading={isFetching} />
                    </Tooltip>
                </div>

                {/* Overall Statistics using SchoolClassroomStatistic component */}
                <SchoolClassroomStatistic
                    mode="overall"
                    title="Bendra klasių statistika"
                    statistics={overallClassroomStatistics} />

                {schoolClassroomStatistics && schoolClassroomStatistics.length > 0 && (
                    <Row gutter={16} style={{ marginTop: '16px' }}>
                        {/* Left side - Schools list */}
                        <Col xs={24} lg={6}>
                            <Card
                                title="Mokyklų sąrašas"
                                extra={(
                                    <Space>
                                        <Select
                                            placeholder="Filtruoti pagal tipą"
                                            value={selectedSchoolType}
                                            onChange={handleSchoolTypeFilter}
                                            allowClear
                                            style={{ width: 150 }}
                                            size="small">
                                            {schoolTypeOptions.map(option => (
                                                <Select.Option key={option.value} value={option.value}>
                                                    {option.label}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                        {selectedSchoolType && (
                                            <Button
                                                size="small"
                                                icon={<FilterOutlined />}
                                                onClick={handleResetFilter}>
                                                Išvalyti
                                            </Button>
                                        )}
                                    </Space>
                                )}>
                                <div style={{ marginBottom: '16px' }}>
                                    <Input
                                        placeholder="Ieškoti pagal pavadinimą..."
                                        prefix={<SearchOutlined />}
                                        value={searchText}
                                        onChange={handleSearchChange}
                                        allowClear
                                        size="small"
                                        style={{ marginBottom: '8px' }} />
                                    {(selectedSchoolType || searchText) && (
                                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                                            Rasta: {filteredSchools.length} mokykla{filteredSchools.length !== 1 ? 's' : ''}
                                        </div>
                                    )}
                                </div>
                                <List
                                    dataSource={filteredSchools}
                                    renderItem={(item) => (
                                        <List.Item
                                            style={{
                                                padding: '8px 0',
                                                cursor: 'pointer',
                                                backgroundColor: selectedSchoolId === item.school_id ? '#f0f0f0' : 'transparent',
                                                borderRadius: '4px',
                                                margin: '2px 0',
                                            }}
                                            onClick={() => handleSchoolClick(item.school_id, item.statistics)}>
                                            <div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                                        <Avatar
                                                            icon={<BankOutlined />}
                                                            style={{ backgroundColor: getSchoolIconColor(item.school_type), marginRight: '12px', width: '40px' }} />
                                                        <div>
                                                            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                                                                {item.school_name}
                                                            </div>
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                Iš viso klasių: {item.statistics.total_classrooms} |
                                                                Iš viso mokinių: {item.statistics.total_students} |
                                                                Vidutiniškai: {item.statistics.average_students_per_classroom.toFixed(1)} mokinių/klasėje
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </List.Item>
                                    )} />
                            </Card>
                        </Col>

                        {/* Right side - School statistics */}
                        <Col xs={24} lg={18}>
                            <div ref={schoolInfoRef}>
                                {selectedSchoolId && classroomStatistics ? (
                                    <Card
                                        title={`${schoolClassroomStatistics?.find(s => s.school_id === selectedSchoolId)?.school_name} - Detali statistika`}
                                        extra={(
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setSelectedSchoolId(null);
                                                    dispatch(setSelectedSchoolStatistics(null));
                                                }}>
                                                Uždaryti
                                            </Button>
                                        )}>
                                        <SchoolClassroomStatistic schoolId={selectedSchoolId} mode="school" />
                                    </Card>
                                ) : (
                                    <Card title="Mokyklos statistika">
                                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                                            <BankOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                                            <p>Pasirinkite mokyklą iš sąrašo, kad pamatytumėte detalią statistiką</p>
                                        </div>
                                    </Card>
                                )}
                            </div>
                        </Col>
                    </Row>
                )}
            </div>
        </Card>
    );
};

export default SchoolClassroomStatisticsOverall;
